import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { ClipboardList, Search, Save, Loader2, ChevronLeft, ChevronRight, MessageSquare, Sun, Moon, AlertTriangle, Copy, MapPin, UserX, UserCheck, Eye, EyeOff, MoreVertical, Trash2, RotateCcw, Layers, CalendarDays, Coffee, X, User, Maximize2, Minimize2, Building2, Home } from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, getWeek } from 'date-fns';
import { it } from 'date-fns/locale';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const DAYS_MAP = {
  0: 'domenica',
  1: 'lunedi',
  2: 'martedi',
  3: 'mercoledi',
  4: 'giovedi',
  5: 'venerdi',
  6: 'sabato'
};

const MEALS = [
  { code: 'colazione', name: 'Colazione', icon: Coffee, color: 'amber', bgColor: 'bg-amber-500', hoverColor: 'hover:bg-amber-600', lightBg: 'bg-amber-50', borderColor: 'border-amber-500' },
  { code: 'pranzo', name: 'Pranzo', icon: Sun, color: 'orange', bgColor: 'bg-orange-500', hoverColor: 'hover:bg-orange-600', lightBg: 'bg-orange-50', borderColor: 'border-orange-500' },
  { code: 'cena', name: 'Cena', icon: Moon, color: 'indigo', bgColor: 'bg-indigo-500', hoverColor: 'hover:bg-indigo-600', lightBg: 'bg-indigo-50', borderColor: 'border-indigo-500' }
];

// Order Modal Component - supports both fullscreen and floating modes
const OrderModal = ({ 
  isOpen, 
  onClose, 
  patient, 
  patients,
  categories, 
  menuItems,
  categoryOptions, // Options for breakfast categories
  orders, 
  notes,
  mealType,
  setMealType,
  diets, // Diets for variant dropdown and patient info
  allergies,
  onOrderToggle,
  onBreakfastOptionSelect, // Handler for selecting breakfast options
  onVariantChange,
  onNoteChange,
  onSave,
  onNavigate,
  onReset,
  onCopyFromPrevious,
  saving,
  dateString,
  selectedDate,
  onDateChange,
  currentIndex,
  totalPatients,
  isFullscreen,
  onToggleFullscreen,
  canEdit = true // Permission to edit orders
}) => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  if (!isOpen || !patient) return null;

  const currentMeal = MEALS.find(m => m.code === mealType);
  const patientOrders = orders[mealType]?.[patient.id] || {};
  const patientNotes = notes[mealType]?.[patient.id] || {};
  const orderCount = Object.values(patientOrders).filter(o => o.selected).length;

  // Get diet name from diets list
  const getDietName = (code) => {
    const diet = diets.find(d => d.code === code);
    return diet?.name || code;
  };

  const getAllergyName = (code) => {
    // Try to find by code first
    let allergy = allergies.find(a => a.code === code);
    if (allergy) return allergy.name;
    
    // Try to find by number (extract number from codes like "1a", "7b", etc.)
    const numberMatch = code.match(/^(\d+)/);
    if (numberMatch) {
      const num = parseInt(numberMatch[1]);
      allergy = allergies.find(a => a.number === num);
      if (allergy) return allergy.name;
    }
    
    return code; // Fallback to code if not found
  };

  // Get all diet options for the dropdown
  // Patient's diet (from their profile) is marked as default
  const getVariantOptions = () => {
    const patientDiet = patient.diet || 'normale'; // Patient's assigned diet from profile
    const options = [];
    
    // Add all diets from the diets list (sorted by number)
    const sortedDiets = [...diets].sort((a, b) => (a.number || 0) - (b.number || 0));
    sortedDiets.forEach(diet => {
      options.push({ 
        value: diet.code, 
        label: diet.name,
        isPatientDefault: diet.code === patientDiet
      });
    });
    
    return options;
  };

  // Filter categories by meal type
  const mealCategories = categories.filter(cat => {
    if (mealType === 'colazione') {
      return cat.meal_type === 'colazione';
    } else {
      return cat.meal_type === 'pranzo_cena' || !cat.meal_type;
    }
  });

  // Floating modal wrapper classes
  const floatingWrapperClasses = "fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4";
  const floatingContentClasses = "bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden w-full max-w-2xl max-h-[90vh]";
  
  // Fullscreen modal wrapper classes  
  const fullscreenWrapperClasses = "fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-sm";
  const fullscreenContentClasses = "fixed inset-0 bg-white flex flex-col overflow-hidden";

  return (
    <div className={isFullscreen ? fullscreenWrapperClasses : floatingWrapperClasses}>
      <div className={isFullscreen ? fullscreenContentClasses : floatingContentClasses}>
        {/* Header */}
        <div className={`${currentMeal.bgColor} text-white px-4 py-3 flex items-center justify-between shadow-lg ${!isFullscreen ? 'rounded-t-2xl' : ''}`}>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onNavigate('prev')}
              className="text-white hover:bg-white/20 h-10 w-10"
              data-testid="modal-prev-patient"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center font-mono text-xl font-bold">
                {patient.room}
              </div>
              <div>
                <h2 className="text-lg font-bold">{patient.name}</h2>
                <div className="flex items-center gap-2 text-white/80 text-sm">
                  <span>{currentIndex + 1} / {totalPatients}</span>
                  {!patient.is_present && (
                    <Badge className="bg-red-500/80 text-white text-xs">
                      Assente
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {orderCount > 0 && (
              <Badge className="bg-white/20 text-white">
                {orderCount} piatti
              </Badge>
            )}
            {/* Toggle fullscreen button - only on desktop */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggleFullscreen}
              className="text-white hover:bg-white/20 h-10 w-10 hidden md:flex"
              title={isFullscreen ? "Modalità fluttuante" : "Schermo intero"}
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20 h-10 w-10"
              data-testid="modal-close"
            >
              <X className="w-6 h-6" />
            </Button>
          </div>
        </div>

        {/* Patient Info Bar */}
        <div className="bg-slate-50 px-4 py-2 border-b flex flex-wrap items-center gap-2">
          {(patient.diets || patient.pathologies || patient.conditions || []).map(code => (
            <Badge key={code} variant="outline" className="bg-blue-50 text-blue-700 text-xs">
              {getDietName(code)}
            </Badge>
          ))}
          {(patient.allergies || []).map(code => (
            <Badge key={`allergy-${code}`} className="bg-orange-100 text-orange-700 text-xs">
              <AlertTriangle className="w-3 h-3 mr-1" />
              {getAllergyName(code)}
            </Badge>
          ))}
          {patient.meal_location && (
            <Badge variant="outline" className={`text-xs ${patient.meal_location === 'ristorante' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
              <MapPin className="w-3 h-3 mr-1" />
              {patient.meal_location === 'ristorante' ? 'Ristorante' : 'Reparto'}
            </Badge>
          )}
        </div>

        {/* Meal Type Tabs */}
        <div className="bg-white border-b px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              {MEALS.map(meal => {
                const Icon = meal.icon;
                const isActive = mealType === meal.code;
                const mealOrders = orders[meal.code]?.[patient.id] || {};
                const mealOrderCount = Object.values(mealOrders).filter(o => o.selected).length;
                
                return (
                  <Button
                    key={meal.code}
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMealType(meal.code)}
                    className={`gap-2 ${isActive ? `${meal.bgColor} ${meal.hoverColor}` : ''}`}
                    data-testid={`modal-meal-${meal.code}`}
                  >
                    <Icon className="w-4 h-4" />
                    {meal.name}
                    {mealOrderCount > 0 && (
                      <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${isActive ? 'bg-white/20' : 'bg-slate-100'}`}>
                        {mealOrderCount}
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              {/* Date Picker */}
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 font-medium"
                    data-testid="modal-date-picker"
                  >
                    <CalendarDays className="w-4 h-4" />
                    <span className="hidden sm:inline">
                      {format(selectedDate, 'd MMM', { locale: it })}
                    </span>
                    <span className="sm:hidden">
                      {format(selectedDate, 'd/M', { locale: it })}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      if (date) {
                        onDateChange(date);
                        setCalendarOpen(false);
                      }
                    }}
                    locale={it}
                  />
                </PopoverContent>
              </Popover>

              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onReset(patient.id)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Cancella Ordini</span>
                </Button>
              )}
              {canEdit && mealType !== 'colazione' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onCopyFromPrevious(patient.id)}
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Copia da {mealType === 'cena' ? 'Pranzo' : 'Colazione'}</span>
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Menu Items - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50">
          <div className="max-w-3xl mx-auto space-y-3">
            {mealCategories.length === 0 ? (
              <div className="text-center py-12">
                <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Nessuna categoria per questo pasto</p>
              </div>
            ) : mealType === 'colazione' ? (
              /* COLAZIONE - Show options to select from each category */
              mealCategories.map(category => {
                const options = categoryOptions[category.code] || [];
                const orderData = patientOrders[category.code];
                const selectedOption = orderData?.selectedOption;
                const noteValue = patientNotes[category.code] || '';

                return (
                  <div 
                    key={category.code}
                    className={`bg-white rounded-xl border-2 transition-all shadow-sm ${
                      selectedOption 
                        ? `${currentMeal.borderColor} ${currentMeal.lightBg}`
                        : 'border-slate-200'
                    }`}
                    data-testid={`modal-category-${category.code}`}
                  >
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-semibold text-slate-900 text-lg">{category.name}</span>
                        {selectedOption && (
                          <Badge className={`${currentMeal.bgColor} text-white`}>
                            {selectedOption}
                          </Badge>
                        )}
                      </div>
                      
                      {options.length === 0 ? (
                        <p className="text-slate-400 text-sm italic">Nessuna opzione configurata</p>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {options.map(option => {
                            const isOptionSelected = selectedOption === option.name;
                            return (
                              <button
                                key={option.id}
                                onClick={() => canEdit && onBreakfastOptionSelect(patient.id, category.code, option)}
                                disabled={!canEdit}
                                className={`p-2 rounded-lg text-left text-sm transition-all ${
                                  isOptionSelected
                                    ? `${currentMeal.bgColor} text-white font-medium`
                                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                                } ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <span className="block truncate">{option.name}</span>
                                {option.allergens?.length > 0 && (
                                  <div className="flex gap-1 mt-1 flex-wrap">
                                    {option.allergens.map(code => (
                                      <span key={code} className={`text-[10px] px-1 rounded ${
                                        isOptionSelected ? 'bg-white/30' : 'bg-amber-100 text-amber-700'
                                      }`}>
                                        {code}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* Note field when option is selected */}
                      {selectedOption && (
                        <div className="mt-3 pt-3 border-t border-slate-200">
                          <Input
                            placeholder="Note per questa scelta..."
                            value={noteValue}
                            onChange={(e) => onNoteChange(patient.id, category.code, e.target.value)}
                            disabled={!canEdit}
                            className="text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              /* PRANZO/CENA - Original logic */
              mealCategories.map(category => {
                const orderData = patientOrders[category.code];
                const isSelected = orderData?.selected;
                const menuData = menuItems[category.code];
                const dishName = menuData?.dish_name;
                const sideDish = menuData?.side_dish;
                const allergens = menuData?.allergens || [];
                const sideDishAllergens = menuData?.side_dish_allergens || [];
                const noteValue = patientNotes[category.code] || '';

                return (
                  <div 
                    key={category.code}
                    className={`bg-white rounded-xl border-2 transition-all shadow-sm ${
                      isSelected 
                        ? `${currentMeal.borderColor} ${currentMeal.lightBg}`
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`modal-category-${category.code}`}
                  >
                    <div 
                      className={`p-4 ${canEdit ? 'cursor-pointer' : 'cursor-default'}`}
                      onClick={() => canEdit && onOrderToggle(patient.id, category.code)}
                    >
                      <div className="flex items-start gap-4">
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => canEdit && onOrderToggle(patient.id, category.code)}
                            disabled={!canEdit}
                            className={`mt-1 h-6 w-6 ${isSelected ? 'border-2' : ''}`}
                            data-testid={`modal-checkbox-${category.code}`}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-slate-900 text-lg">{category.name}</span>
                          </div>
                          {/* Piatto principale */}
                          <p className="text-slate-500 mt-1">
                            {dishName || 'Menu non definito'}
                          </p>
                          {/* Allergeni del piatto principale */}
                          {allergens.length > 0 && (
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                              <div className="flex flex-wrap gap-1">
                                {allergens.map(allergenCode => (
                                  <span 
                                    key={allergenCode}
                                    className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium"
                                    title={getAllergyName(allergenCode)}
                                  >
                                    {allergenCode.replace(/[ab]$/, '')} - {getAllergyName(allergenCode)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Contorno */}
                          {sideDish && (
                            <div className="mt-2 pl-2 border-l-2 border-green-300">
                              <p className="text-sm text-green-700">
                                <span className="font-medium">Contorno:</span> {sideDish}
                              </p>
                              {/* Allergeni del contorno */}
                              {sideDishAllergens.length > 0 && (
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0" />
                                  <div className="flex flex-wrap gap-1">
                                    {sideDishAllergens.map(allergenCode => (
                                      <span 
                                        key={`side-${allergenCode}`}
                                        className="text-xs px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 font-medium"
                                        title={getAllergyName(allergenCode)}
                                      >
                                        {allergenCode.replace(/[ab]$/, '')} - {getAllergyName(allergenCode)}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded options when selected */}
                    {isSelected && (
                      <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-medium text-slate-500 mb-1 block">
                              Consistenza/Dieta Piatto
                            </label>
                            <Select
                              value={orderData?.variant || 'normale'}
                              onValueChange={(v) => onVariantChange(patient.id, category.code, v)}
                            >
                              <SelectTrigger className="h-10" data-testid={`modal-variant-${category.code}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {getVariantOptions().map(opt => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    <span className="flex items-center gap-2">
                                      {opt.label}
                                      {opt.isPatientDefault && (
                                        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                                          Paziente
                                        </span>
                                      )}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-500 mb-1 block">Note</label>
                            <div className="relative">
                              <Input
                                placeholder="Aggiungi nota..."
                                value={noteValue}
                                onChange={(e) => onNoteChange(patient.id, category.code, e.target.value)}
                                className="h-10 pr-8"
                                data-testid={`modal-note-${category.code}`}
                              />
                              {noteValue && (
                                <MessageSquare className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-500" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer with Navigation and Save */}
        <div className="bg-white border-t px-4 py-3 pb-20 sm:pb-4 shadow-lg">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => onNavigate('prev')}
              className="gap-2"
              data-testid="modal-footer-prev"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">Camera Prec.</span>
            </Button>

            {canEdit ? (
              <Button
                onClick={() => onSave(patient.id)}
                disabled={saving}
                className={`gap-2 px-8 ${currentMeal.bgColor} ${currentMeal.hoverColor}`}
                data-testid="modal-save"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <Save className="w-5 h-5" />
                )}
                Salva {currentMeal.name}
              </Button>
            ) : (
              <div className="text-sm text-slate-500 bg-slate-100 px-4 py-2 rounded-lg">
                Solo visualizzazione
              </div>
            )}

            <Button
              variant="outline"
              onClick={() => onNavigate('next')}
              className="gap-2"
              data-testid="modal-footer-next"
            >
              <span className="hidden sm:inline">Camera Succ.</span>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const OrdersPage = () => {
  const { api, canEditOrders, activeStructure, isSuperAdmin, loading: authLoading } = useAuth();
  const [patients, setPatients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [diets, setDiets] = useState([]); // Diets for variant dropdown and patient info
  const [allergies, setAllergies] = useState([]);
  const [menuItems, setMenuItems] = useState({});
  const [categoryOptions, setCategoryOptions] = useState({}); // Options for breakfast categories
  const [orders, setOrders] = useState({ colazione: {}, pranzo: {}, cena: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [mealType, setMealType] = useState('pranzo');
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [notes, setNotes] = useState({ colazione: {}, pranzo: {}, cena: {} });
  const [showAbsent, setShowAbsent] = useState(false);
  const [locationFilter, setLocationFilter] = useState('all');
  const [floorFilter, setFloorFilter] = useState('all');
  const [houseFilter, setHouseFilter] = useState('all'); // Filtro per casa (multi-casa)
  const [calendarOpen, setCalendarOpen] = useState(false);
  
  // Multi-house support - memoize to prevent infinite re-renders
  const isMultiHouse = activeStructure?.is_multi_house || false;
  const houses = useMemo(() => 
    (activeStructure?.houses || []).filter(h => h.requires_meal_service),
    [activeStructure?.houses]
  ); // Solo case con servizio pasti
  
  // Modal display mode: fullscreen on mobile, user preference on desktop
  const [isFullscreen, setIsFullscreen] = useState(() => {
    // Default to fullscreen on mobile, floating on desktop
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('orderModalFullscreen');
      if (saved !== null) return saved === 'true';
      return window.innerWidth < 768;
    }
    return true;
  });
  
  // Allergy alert dialog state
  const [allergyAlert, setAllergyAlert] = useState(null);
  
  // Ref to prevent multiple error toasts
  const errorShownRef = useRef(false);

  const toggleFullscreen = () => {
    const newValue = !isFullscreen;
    setIsFullscreen(newValue);
    localStorage.setItem('orderModalFullscreen', String(newValue));
  };

  const dayCode = DAYS_MAP[selectedDate.getDay()];
  const weekNumber = getWeek(selectedDate, { weekStartsOn: 1 });
  const dateString = format(selectedDate, 'yyyy-MM-dd');

  const navigateDate = (direction) => {
    setSelectedDate(prev => addDays(prev, direction));
  };

  const handleDateSelect = (date) => {
    if (date) {
      setSelectedDate(date);
      setCalendarOpen(false);
    }
  };

  const fetchData = useCallback(async () => {
    // Guard: superadmin must have an active structure selected
    if (isSuperAdmin && !activeStructure?.id) {
      setLoading(false);
      return;
    }
    
    // Reset error shown flag on new fetch
    errorShownRef.current = false;
    
    setLoading(true);
    try {
      // Build params for patients - filter by house if multi-house
      const patientParams = {};
      if (houseFilter && houseFilter !== 'all') {
        patientParams.house_id = houseFilter;
      }
      
      const [patientsRes, categoriesRes, dietsRes, allergiesRes, colazioneOrdersRes, pranzoOrdersRes, cenaOrdersRes] = await Promise.all([
        api.get('/patients', { params: patientParams }),
        api.get('/categories'),
        api.get('/diets'), // Diets for variant dropdown and patient info
        api.get('/allergies'),
        api.get('/orders', { params: { date: dateString, meal_type: 'colazione' } }),
        api.get('/orders', { params: { date: dateString, meal_type: 'pranzo' } }),
        api.get('/orders', { params: { date: dateString, meal_type: 'cena' } })
      ]);

      // Load menus for all meal types
      const currentYear = new Date().getFullYear();
      const [colazioneMenuRes, pranzoCenaMenuRes] = await Promise.all([
        api.get('/menu', { params: { week_number: weekNumber, year: currentYear, day: dayCode, meal_type: 'colazione' } }),
        api.get('/menu', { params: { week_number: weekNumber, year: currentYear, day: dayCode, meal_type: 'pranzo' } })
      ]);

      // Filter patients: exclude those in houses without meal service (apartments)
      let filteredPatients = patientsRes.data;
      if (isMultiHouse && houses.length > 0) {
        const mealHouseIds = houses.map(h => h.id);
        filteredPatients = patientsRes.data.filter(p => 
          !p.house_id || mealHouseIds.includes(p.house_id)
        );
      }
      
      setPatients(filteredPatients);
      const allCategories = categoriesRes.data;
      setCategories(allCategories);
      setDiets(dietsRes.data); // Diets for variant dropdown and patient info
      setAllergies(allergiesRes.data);

      // Load options for breakfast categories
      const breakfastCategories = allCategories.filter(c => c.meal_type === 'colazione');
      const categoryOptionsMap = {};
      
      // Fetch options for each breakfast category
      await Promise.all(breakfastCategories.map(async (cat) => {
        try {
          const optionsRes = await api.get(`/categories/${cat.id}/options`);
          categoryOptionsMap[cat.code] = optionsRes.data;
        } catch (err) {
          categoryOptionsMap[cat.code] = [];
        }
      }));
      setCategoryOptions(categoryOptionsMap);

      // Combine menus from both meal types
      const menuByCategory = {};
      [...colazioneMenuRes.data, ...pranzoCenaMenuRes.data].forEach(item => {
        menuByCategory[item.category_code] = {
          dish_name: item.dish_name,
          allergens: item.allergens || [],
          side_dish: item.side_dish,
          side_dish_allergens: item.side_dish_allergens || []
        };
      });
      setMenuItems(menuByCategory);

      // Group orders by meal_type, patient_id and category
      const colazioneOrders = {};
      const pranzoOrders = {};
      const cenaOrders = {};
      const colazioneNotes = {};
      const pranzoNotes = {};
      const cenaNotes = {};

      colazioneOrdersRes.data.forEach(order => {
        if (!colazioneOrders[order.patient_id]) colazioneOrders[order.patient_id] = {};
        colazioneOrders[order.patient_id][order.category_code] = {
          selected: true,
          selectedOption: order.dish_name, // For breakfast, dish_name is the selected option
          variant: order.condition_variant,
          note: order.note || '',
          allergens: order.allergens || []
        };
        if (order.note) {
          if (!colazioneNotes[order.patient_id]) colazioneNotes[order.patient_id] = {};
          colazioneNotes[order.patient_id][order.category_code] = order.note;
        }
      });

      pranzoOrdersRes.data.forEach(order => {
        if (!pranzoOrders[order.patient_id]) pranzoOrders[order.patient_id] = {};
        pranzoOrders[order.patient_id][order.category_code] = {
          selected: true,
          variant: order.condition_variant,
          note: order.note || '',
          allergens: order.allergens || []
        };
        if (order.note) {
          if (!pranzoNotes[order.patient_id]) pranzoNotes[order.patient_id] = {};
          pranzoNotes[order.patient_id][order.category_code] = order.note;
        }
      });

      cenaOrdersRes.data.forEach(order => {
        if (!cenaOrders[order.patient_id]) cenaOrders[order.patient_id] = {};
        cenaOrders[order.patient_id][order.category_code] = {
          selected: true,
          variant: order.condition_variant,
          note: order.note || '',
          allergens: order.allergens || []
        };
        if (order.note) {
          if (!cenaNotes[order.patient_id]) cenaNotes[order.patient_id] = {};
          cenaNotes[order.patient_id][order.category_code] = order.note;
        }
      });

      setOrders({ colazione: colazioneOrders, pranzo: pranzoOrders, cena: cenaOrders });
      setNotes({ colazione: colazioneNotes, pranzo: pranzoNotes, cena: cenaNotes });

    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Only show error toast once per fetch attempt
      if (!errorShownRef.current) {
        errorShownRef.current = true;
        toast.error('Errore nel caricamento dati');
      }
    } finally {
      setLoading(false);
    }
  }, [api, weekNumber, dayCode, dateString, houseFilter, isMultiHouse, houses, isSuperAdmin, activeStructure]);

  useEffect(() => {
    // Don't fetch while auth is still loading
    if (authLoading) return;
    fetchData();
  }, [fetchData, authLoading]);

  // Filter patients
  const filteredPatients = patients.filter(patient => {
    if (patient.status === 'archiviato' || patient.status === 'cestino') return false;
    
    const matchesSearch = patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          patient.room.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLocation = locationFilter === 'all' || patient.meal_location === locationFilter;
    
    const floor = patient.room ? patient.room.charAt(0) : '';
    const matchesFloor = floorFilter === 'all' || floor === floorFilter;
    
    const matchesPresence = showAbsent || patient.is_present;
    
    return matchesSearch && matchesLocation && matchesFloor && matchesPresence;
  }).sort((a, b) => {
    const roomA = parseInt(a.room) || 0;
    const roomB = parseInt(b.room) || 0;
    return roomA - roomB;
  });

  const availableFloors = [...new Set(patients.map(p => p.room?.charAt(0)).filter(Boolean))].sort();
  const presentCount = filteredPatients.filter(p => p.is_present).length;
  const absentCount = filteredPatients.filter(p => !p.is_present).length;

  const getDietColor = (code) => {
    const diet = diets.find(d => d.code === code);
    return diet?.color || '#3b82f6';
  };

  const getDietName = (code) => {
    const diet = diets.find(d => d.code === code);
    return diet?.name || code;
  };

  const getAllergyName = (code) => {
    // Try to find by code first
    let allergy = allergies.find(a => a.code === code);
    if (allergy) return allergy.name;
    
    // Try to find by number (extract number from codes like "1a", "7b", etc.)
    const numberMatch = code.match(/^(\d+)/);
    if (numberMatch) {
      const num = parseInt(numberMatch[1]);
      allergy = allergies.find(a => a.number === num);
      if (allergy) return allergy.name;
    }
    
    return code; // Fallback to code if not found
  };

  // Handle order toggle with allergy check
  const handleOrderToggle = (patientId, categoryCode, skipAllergyCheck = false) => {
    const menuData = menuItems[categoryCode] || {};
    const patient = patients.find(p => p.id === patientId);
    const patientAllergies = patient?.allergies || [];
    
    // Get current order state
    const mealOrders = orders[mealType] || {};
    const patientOrders = mealOrders[patientId] || {};
    const currentOrder = patientOrders[categoryCode];
    
    // If selecting (not deselecting) and not skipping allergy check
    if (!currentOrder?.selected && !skipAllergyCheck && patientAllergies.length > 0) {
      // Get all allergens from the dish (main dish + side dish)
      const dishAllergens = [...(menuData.allergens || []), ...(menuData.side_dish_allergens || [])];
      
      // Helper function to get allergen number from code or name
      const getAllergenNumber = (allergenInput) => {
        // If it's already a number string like "1", "1a", "1b", extract the number
        const numMatch = String(allergenInput).match(/^(\d+)/);
        if (numMatch) return parseInt(numMatch[1]);
        
        // Otherwise, look it up in the allergies list by code or name
        const allergy = allergies.find(a => 
          a.code?.toLowerCase() === String(allergenInput).toLowerCase() ||
          a.name?.toLowerCase() === String(allergenInput).toLowerCase()
        );
        return allergy?.number || null;
      };
      
      // Check for conflicts
      const conflicts = [];
      patientAllergies.forEach(patientAllergen => {
        const patientAllergenNum = getAllergenNumber(patientAllergen);
        
        dishAllergens.forEach(dishAllergen => {
          const dishAllergenNum = getAllergenNumber(dishAllergen);
          
          // Match if numbers are equal (both resolved to the same allergen number)
          if (patientAllergenNum !== null && dishAllergenNum !== null && 
              patientAllergenNum === dishAllergenNum) {
            // Get the allergy name for display
            const allergyInfo = allergies.find(a => a.number === dishAllergenNum);
            const allergyName = allergyInfo?.name || dishAllergen;
            
            if (!conflicts.includes(allergyName)) {
              conflicts.push(allergyName);
            }
          }
        });
      });
      
      // If conflicts found, show warning dialog
      if (conflicts.length > 0) {
        setAllergyAlert({
          patientId,
          patientName: patient?.name || 'Paziente',
          categoryCode,
          dishName: menuData.dish_name || categoryCode,
          conflicts
        });
        return; // Don't toggle yet - wait for user confirmation
      }
    }
    
    // Proceed with toggle
    setOrders(prev => {
      const mealOrders = prev[mealType] || {};
      const patientOrders = mealOrders[patientId] || {};
      const currentOrder = patientOrders[categoryCode];
      
      if (currentOrder?.selected) {
        // Deselect - remove from local state
        const { [categoryCode]: removed, ...rest } = patientOrders;
        return { 
          ...prev, 
          [mealType]: { ...mealOrders, [patientId]: rest }
        };
      } else {
        // Select - add to local state
        return { 
          ...prev, 
          [mealType]: { 
            ...mealOrders, 
            [patientId]: {
              ...patientOrders,
              [categoryCode]: { selected: true, variant: 'normale', allergens: menuData.allergens || [] }
            }
          }
        };
      }
    });
  };

  // Handle breakfast option selection
  const handleBreakfastOptionSelect = (patientId, categoryCode, option) => {
    const patient = patients.find(p => p.id === patientId);
    const patientAllergies = patient?.allergies || [];
    
    // Get current selection
    const currentSelection = orders[mealType]?.[patientId]?.[categoryCode]?.selectedOption;
    
    // If clicking on same option, deselect it
    if (currentSelection === option.name) {
      setOrders(prev => {
        const mealOrders = prev[mealType] || {};
        const patientOrders = mealOrders[patientId] || {};
        const { [categoryCode]: removed, ...rest } = patientOrders;
        return { 
          ...prev, 
          [mealType]: { ...mealOrders, [patientId]: rest }
        };
      });
      return;
    }
    
    // Check for allergen conflicts
    if (patientAllergies.length > 0 && option.allergens?.length > 0) {
      const getAllergenNumber = (allergenInput) => {
        const numMatch = String(allergenInput).match(/^(\d+)/);
        if (numMatch) return parseInt(numMatch[1]);
        const allergy = allergies.find(a => 
          a.code?.toLowerCase() === String(allergenInput).toLowerCase() ||
          a.name?.toLowerCase() === String(allergenInput).toLowerCase()
        );
        return allergy?.number || null;
      };
      
      const conflicts = [];
      patientAllergies.forEach(patientAllergen => {
        const patientAllergenNum = getAllergenNumber(patientAllergen);
        option.allergens.forEach(optionAllergen => {
          const optionAllergenNum = getAllergenNumber(optionAllergen);
          if (patientAllergenNum !== null && optionAllergenNum !== null && 
              patientAllergenNum === optionAllergenNum) {
            const allergyInfo = allergies.find(a => a.number === optionAllergenNum);
            const allergyName = allergyInfo?.name || optionAllergen;
            if (!conflicts.includes(allergyName)) conflicts.push(allergyName);
          }
        });
      });
      
      if (conflicts.length > 0) {
        setAllergyAlert({
          patientId,
          patientName: patient?.name || 'Paziente',
          categoryCode,
          dishName: option.name,
          conflicts,
          isBreakfast: true,
          option: option
        });
        return;
      }
    }
    
    // Select the option
    setOrders(prev => ({
      ...prev,
      [mealType]: {
        ...prev[mealType],
        [patientId]: {
          ...prev[mealType]?.[patientId],
          [categoryCode]: { 
            selected: true, 
            selectedOption: option.name,
            allergens: option.allergens || []
          }
        }
      }
    }));
  };

  const handleVariantChange = (patientId, categoryCode, variant) => {
    setOrders(prev => ({
      ...prev,
      [mealType]: {
        ...prev[mealType],
        [patientId]: {
          ...prev[mealType]?.[patientId],
          [categoryCode]: {
            ...prev[mealType]?.[patientId]?.[categoryCode],
            variant
          }
        }
      }
    }));
  };

  const handleNoteChange = (patientId, categoryCode, note) => {
    setNotes(prev => ({
      ...prev,
      [mealType]: {
        ...prev[mealType],
        [patientId]: {
          ...prev[mealType]?.[patientId],
          [categoryCode]: note
        }
      }
    }));
  };

  const handleResetPatientOrders = async (patientId) => {
    try {
      await api.delete('/orders', { 
        params: { patient_id: patientId, date: dateString, meal_type: mealType }
      });
      setOrders(prev => ({
        ...prev,
        [mealType]: {
          ...(prev[mealType] || {}),
          [patientId]: {}
        }
      }));
      setNotes(prev => ({
        ...prev,
        [mealType]: {
          ...(prev[mealType] || {}),
          [patientId]: {}
        }
      }));
      toast.success('Ordini resettati');
    } catch (error) {
      toast.error('Errore nel reset');
    }
  };

  const copyFromPreviousMeal = (patientId) => {
    const sourceMeal = mealType === 'cena' ? 'pranzo' : 'colazione';
    const sourceOrders = orders[sourceMeal]?.[patientId] || {};
    const sourceNotes = notes[sourceMeal]?.[patientId] || {};
    
    if (Object.keys(sourceOrders).length === 0) {
      toast.info(`Nessun ordine da copiare da ${sourceMeal}`);
      return;
    }
    
    setOrders(prev => ({
      ...prev,
      [mealType]: {
        ...prev[mealType],
        [patientId]: { ...sourceOrders }
      }
    }));
    
    setNotes(prev => ({
      ...prev,
      [mealType]: {
        ...prev[mealType],
        [patientId]: { ...sourceNotes }
      }
    }));
    
    toast.success(`Ordini copiati da ${sourceMeal}`);
  };

  const navigatePatient = (direction) => {
    if (!selectedPatientId) return;
    const currentIndex = filteredPatients.findIndex(p => p.id === selectedPatientId);
    if (currentIndex === -1) return;
    
    // Calculate new patient index
    let newIndex;
    if (direction === 'next') {
      newIndex = currentIndex + 1 < filteredPatients.length ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : filteredPatients.length - 1;
    }
    
    // Simply navigate to new patient
    // The orders state already contains data loaded from DB at page load
    // If patient has saved orders, they will show. If not, it will be empty.
    setSelectedPatientId(filteredPatients[newIndex].id);
  };

  const savePatientOrders = async (patientId) => {
    setSaving(true);
    try {
      const patientOrders = orders[mealType]?.[patientId] || {};
      const patientNotes = notes[mealType]?.[patientId] || {};
      const patient = patients.find(p => p.id === patientId);
      const items = [];

      Object.entries(patientOrders).forEach(([categoryCode, order]) => {
        if (order.selected) {
          // For breakfast, use selectedOption as dish_name
          // For lunch/dinner, use menuItems
          if (mealType === 'colazione') {
            items.push({
              category_code: categoryCode,
              dish_name: order.selectedOption || categoryCode,
              allergens: order.allergens || [],
              condition_variant: 'normale',
              note: patientNotes[categoryCode] || ''
            });
          } else {
            const menuData = menuItems[categoryCode] || {};
            items.push({
              category_code: categoryCode,
              dish_name: menuData.dish_name || categoryCode,
              allergens: menuData.allergens || [],
              condition_variant: order.variant || 'normale',
              note: patientNotes[categoryCode] || ''
            });
          }
        }
      });

      // Only proceed if there are items to save
      if (items.length > 0) {
        // First delete existing orders, then save new ones
        await api.delete('/orders/patient-meal', {
          params: {
            patient_id: patientId,
            date: dateString,
            meal_type: mealType
          }
        });

        await api.post('/orders/bulk', {
          patient_id: patientId,
          patient_name: patient?.name || '',
          patient_room: patient?.room || '',
          patient_conditions: patient?.diets || patient?.pathologies || patient?.conditions || [],
          week_number: weekNumber,
          day: dayCode,
          date: dateString,
          meal_type: mealType,
          items
        });
        toast.success(`Salvato: ${items.length} ${mealType === 'colazione' ? 'scelte' : 'portate'}`);
      } else {
        toast.info('Nessuna selezione da salvare');
      }
    } catch (error) {
      console.error('Failed to save orders:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // Close modal - no auto-save, user must click Save explicitly
  const handleCloseModal = () => {
    setSelectedPatientId(null);
  };

  const selectedPatient = patients.find(p => p.id === selectedPatientId);
  const currentPatientIndex = filteredPatients.findIndex(p => p.id === selectedPatientId);

  // Show message if superadmin hasn't selected a structure
  if (isSuperAdmin && !activeStructure?.id) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center px-4">
        <Building2 className="w-12 h-12 text-slate-400 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700 mb-2">Nessuna struttura selezionata</h2>
        <p className="text-slate-500">Seleziona una struttura dalla pagina Strutture per visualizzare le comande.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardList className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Comande</h1>
            <p className="text-slate-500">Gestisci gli ordini dei pazienti</p>
          </div>
        </div>
      </div>

      {/* Date Navigation */}
      <Card className="border-slate-200">
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigateDate(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="min-w-[200px] justify-start gap-2">
                    <CalendarDays className="w-4 h-4" />
                    {format(selectedDate, "EEEE d MMMM", { locale: it })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    locale={it}
                  />
                </PopoverContent>
              </Popover>
              
              <Button variant="outline" size="icon" onClick={() => navigateDate(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Meal Type Selector */}
            <div className="flex gap-1">
              {MEALS.map(meal => {
                const Icon = meal.icon;
                const isActive = mealType === meal.code;
                return (
                  <Button
                    key={meal.code}
                    variant={isActive ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setMealType(meal.code)}
                    className={isActive ? `${meal.bgColor} ${meal.hoverColor}` : ''}
                  >
                    <Icon className="w-4 h-4 mr-1" />
                    {meal.name}
                  </Button>
                );
              })}
            </div>

            <Badge variant="outline" className="bg-slate-50">
              Settimana {weekNumber}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Cerca paziente o camera..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Filtro Casa - solo per strutture multi-casa con servizio pasti */}
        {isMultiHouse && houses.length > 0 && (
          <Select value={houseFilter} onValueChange={setHouseFilter}>
            <SelectTrigger className={`w-44 ${houseFilter !== 'all' ? 'border-purple-300 bg-purple-50' : ''}`}>
              <Building2 className={`w-4 h-4 mr-2 ${houseFilter !== 'all' ? 'text-purple-600' : ''}`} />
              <SelectValue placeholder="Casa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                <span className="flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Tutte le case
                </span>
              </SelectItem>
              {houses.map(house => (
                <SelectItem key={house.id} value={house.id}>
                  <span className="flex items-center gap-2">
                    <span className="font-medium">{house.prefix}</span> - {house.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={locationFilter} onValueChange={setLocationFilter}>
          <SelectTrigger className="w-36">
            <MapPin className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="reparto">Reparto</SelectItem>
            <SelectItem value="ristorante">Ristorante</SelectItem>
          </SelectContent>
        </Select>

        <Select value={floorFilter} onValueChange={setFloorFilter}>
          <SelectTrigger className="w-36">
            <Layers className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i piani</SelectItem>
            {availableFloors.map(floor => (
              <SelectItem key={floor} value={floor}>Piano {floor}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant={showAbsent ? "default" : "outline"}
          size="sm"
          onClick={() => setShowAbsent(!showAbsent)}
          className={showAbsent ? "bg-red-500 hover:bg-red-600" : ""}
        >
          {showAbsent ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
          Assenti
        </Button>

        <div className="flex gap-2 ml-auto">
          <Badge variant="outline" className="bg-emerald-50 text-emerald-600 border-emerald-200">
            <UserCheck className="w-3 h-3 mr-1" />
            {presentCount}
          </Badge>
          {absentCount > 0 && (
            <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200">
              <UserX className="w-3 h-3 mr-1" />
              {absentCount}
            </Badge>
          )}
        </div>
      </div>

      {/* Patients Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filteredPatients.map((patient) => {
          const patientOrders = orders[mealType]?.[patient.id] || {};
          const orderCount = Object.values(patientOrders).filter(o => o.selected).length;
          const currentMeal = MEALS.find(m => m.code === mealType);

          return (
            <Card 
              key={patient.id} 
              className={`cursor-pointer transition-all hover:shadow-md ${!patient.is_present ? 'opacity-60' : ''} ${orderCount > 0 ? `ring-2 ${currentMeal.borderColor.replace('border', 'ring')}` : ''}`}
              onClick={() => setSelectedPatientId(patient.id)}
              data-testid={`patient-card-${patient.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center font-mono text-xl font-bold ${
                    patient.is_present ? 'bg-slate-100 text-slate-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {patient.room}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{patient.name}</div>
                    <div className="flex items-center gap-2 mt-1">
                      {!patient.is_present && (
                        <Badge variant="outline" className="bg-red-50 text-red-600 border-red-200 text-xs py-0">
                          Assente
                        </Badge>
                      )}
                      {orderCount > 0 && (
                        <Badge className={`${currentMeal.bgColor} text-xs py-0`}>
                          {orderCount} piatti
                        </Badge>
                      )}
                    </div>
                    {(patient.allergies || []).length > 0 && (
                      <div className="flex items-center gap-1 mt-1">
                        <AlertTriangle className="w-3 h-3 text-orange-500" />
                        <span className="text-xs text-orange-600 truncate">
                          {patient.allergies.slice(0, 2).map(a => getAllergyName(a)).join(', ')}
                          {patient.allergies.length > 2 && '...'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredPatients.length === 0 && (
        <div className="text-center py-12">
          <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nessun paziente trovato</p>
        </div>
      )}

      {/* Order Modal - supports fullscreen and floating modes */}
      <OrderModal
        key={selectedPatientId} // Force re-render when patient changes
        isOpen={!!selectedPatientId}
        onClose={handleCloseModal}
        patient={selectedPatient}
        patients={patients}
        categories={categories}
        menuItems={menuItems}
        categoryOptions={categoryOptions}
        orders={orders}
        notes={notes}
        mealType={mealType}
        setMealType={setMealType}
        diets={diets}
        allergies={allergies}
        onOrderToggle={handleOrderToggle}
        onBreakfastOptionSelect={handleBreakfastOptionSelect}
        onVariantChange={handleVariantChange}
        onNoteChange={handleNoteChange}
        onSave={savePatientOrders}
        onNavigate={navigatePatient}
        onReset={handleResetPatientOrders}
        onCopyFromPrevious={copyFromPreviousMeal}
        saving={saving}
        dateString={dateString}
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        currentIndex={currentPatientIndex}
        totalPatients={filteredPatients.length}
        isFullscreen={isFullscreen}
        onToggleFullscreen={toggleFullscreen}
        canEdit={canEditOrders}
      />

      {/* Allergy Alert Dialog */}
      <Dialog open={!!allergyAlert} onOpenChange={() => setAllergyAlert(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Attenzione: Allergia Rilevata
            </DialogTitle>
            <DialogDescription className="pt-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-2">
                <p><strong>Paziente:</strong> {allergyAlert?.patientName}</p>
                <p><strong>Piatto:</strong> {allergyAlert?.dishName}</p>
                <p className="text-amber-700">
                  <strong>Allergeni presenti:</strong> {allergyAlert?.conflicts?.join(', ')}
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAllergyAlert(null)}>
              Annulla
            </Button>
            <Button 
              onClick={() => {
                if (allergyAlert) {
                  if (allergyAlert.isBreakfast && allergyAlert.option) {
                    // For breakfast, directly set the order with the option
                    setOrders(prev => ({
                      ...prev,
                      [mealType]: {
                        ...prev[mealType],
                        [allergyAlert.patientId]: {
                          ...prev[mealType]?.[allergyAlert.patientId],
                          [allergyAlert.categoryCode]: { 
                            selected: true, 
                            selectedOption: allergyAlert.option.name,
                            allergens: allergyAlert.option.allergens || []
                          }
                        }
                      }
                    }));
                  } else {
                    handleOrderToggle(allergyAlert.patientId, allergyAlert.categoryCode, true); // Skip allergy check
                  }
                  setAllergyAlert(null);
                }
              }}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Procedi Comunque
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
